import { inject, injectable } from 'inversify';
import type * as THREE from 'three';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { SliceType } from '@/engine/app/modules/images/interfaces';
import { TYPES } from '@/engine/ioc';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';

@injectable()
export class CurvedMprController {
  private readonly _imagesService: ImagesService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private _max?: number;

  private _idx?: number;

  private _sliceType?: SliceType;

  private _pts?: Array<THREE.Vector3>;

  public constructor(
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm
  ) {
    this._imagesService = imagesService;
    this._planningAlgorithm = planningAlgorithm;
  }

  public get max(): number {
    return this._max ?? 0;
  }

  public get index(): number {
    return this._idx ?? 0;
  }

  public setCurve(centerLineDescriptor: string, sliceType: SliceType): void {
    this._sliceType = sliceType;
    const pts = this._planningAlgorithm.getValueStrict(centerLineDescriptor, DT.Polyline).value.points;
    this._pts = pts;
    this._max = this._pts.length - 2;
    this.setIndex(Math.round((this._max - 1) / 2));
  }

  public setIndex(idx?: number): void {
    this._idx = idx;
    if (this._pts && this._idx !== undefined && this._sliceType) {
      if (this._idx < 0 && this._idx >= this._pts.length - 2) {
        throw new Error('Index out of bound');
      }
      const origin = this._pts[this._idx];
      const normal = this._pts[this._idx + 1].clone().sub(origin);
      this._imagesService.syncSliceWithPlane(this._sliceType, origin, normal, undefined, undefined, true);
    } else {
      this._imagesService.resetMpr(true);
    }
  }
}
